import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TabStates } from '@utilities/tab-states';
import { combineLatest, distinctUntilChanged, map, Observable, of, startWith, withLatestFrom } from 'rxjs';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { catchError, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { UserConfigsService } from '@services/user-configs.service';
import { iFrameUrlFactory } from '@utilities/iframe-url-factory';
import { HttpClient } from "@angular/common/http";
import { BaseComponent } from "../../../base.component";
import { DocServeService } from "@services/doc-serve.service";
import { Store } from "@ngrx/store";
import { RedactionAnnotationEdit } from "../../../review/redaction-annotation/redaction-annotation-edit.model";
import { cloneDeep } from "lodash-es";
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { annotationEditorMode, annotationsForCurrentPage, currentAnnotationEditorPage, docHasAnnotationImages, isAnnotationEditorProcessing, isManualAnnotation, pagesAsAnArray } from '@store/doc-viewer/annotation-editor/annotation-editor.selectors';
import { annotationEditorActions } from '@store/doc-viewer/annotation-editor/annotation-editor.actions';

@Component({
    selector: 'lex-doc-viewer-annotation-content',
    templateUrl: './doc-viewer-annotation-content.component.html',
    styleUrls: ['./doc-viewer-annotation-content.component.scss'],
    standalone: false
})
export class DocViewerAnnotationContentComponent extends BaseComponent implements OnInit {
	public editorMode$ = this.store.select(annotationEditorMode);
	public showAnnotatedPdf: boolean = false;
	public currentDocumentSnapshot: any;
	public editImageSource: string | ArrayBuffer;
	public currentPageNumber$ = this.store.select(currentAnnotationEditorPage);
	public annoationsForCurrentPage$ = this.store.select(annotationsForCurrentPage);
	public currentPageNumber: number;
	public pages$: Observable<number[]> = this.store.select(pagesAsAnArray);
	public isContextPanelResizing: boolean;
	public currentEdits: RedactionAnnotationEdit[];
	public currentPageDisplayValue: string;
	public url$: Observable<string> = this.docViewerStore.currentDocument$.pipe(
		distinctUntilChanged((prevDoc, currentDoc) =>
			prevDoc.docId === currentDoc.docId
		),
		switchMap((currentDocument) => {
			return this.httpClient.get(
				this.iFrameUrlFactory.buildUrlV2({
					documentId: currentDocument.docId,
					documentView: 'annotated',
					shouldHideToolbar: true
				}),
				{ responseType: 'blob', observe: 'response' }
			).pipe(
				map((result) => URL.createObjectURL(result.body)),
				catchError(() => of(null)),
				startWith(null)
			);
		}),
		takeUntil(this.destroy$)
	);

	private manualAnnotation$ = this.store.select(isManualAnnotation);

	@ViewChild(CdkVirtualScrollViewport) cdkViewPort: CdkVirtualScrollViewport;
	@ViewChild('annotationEditorImage') annotationEditorImage: ElementRef;

	constructor(
		public tabStates: TabStates,
		public userConfigsService: UserConfigsService,
		public docServeService: DocServeService,
		private docViewerStore: DocViewerStore,
		private iFrameUrlFactory: iFrameUrlFactory,
		private httpClient: HttpClient,
		private store: Store,
		private element: ElementRef,
		private cdr: ChangeDetectorRef
	) {
		super();
	}

	public ngOnInit(): void {

		combineLatest([
			this.currentPageNumber$,
			this.store.select(docHasAnnotationImages),
			this.store.select(isAnnotationEditorProcessing),
			this.docViewerStore.currentDocument$
		]).pipe(
			filter(([, hasImages, isProcessing,]) => !isProcessing && hasImages),
			distinctUntilChanged(([aPage, aHasImages, , aDoc], [bPage, bHasImages, , bDoc]) => {
				return aDoc.docId === bDoc.docId
					&& aPage === bPage
					&& aHasImages === bHasImages
			}),
			tap(() => {
				this.editImageSource = null;
				this.cdr.detectChanges();
			}),
			switchMap(([page, , , currentDocument]) => this.docServeService.getAnnotationImage(currentDocument.docId, page).pipe(
					catchError(() => {
						return of(null)
					})
				)
			),
			takeUntil(this.destroy$)
		).subscribe((image) => {
			if (image) {
				const reader = new FileReader();
				reader.addEventListener('load', () => {
					this.editImageSource = reader.result;
					this.cdr.detectChanges();
				}, false);
				reader.readAsDataURL(image);
			}
		});

		this.annoationsForCurrentPage$.pipe(
			takeUntil(this.destroy$)
		).subscribe((x) => {
			this.currentEdits = cloneDeep(x);
		});

		this.currentPageNumber$.pipe(
			withLatestFrom(this.pages$),
			takeUntil(this.destroy$)
		).subscribe(([currentPage, pages]) => {
			this.currentPageNumber = currentPage;
			this.currentPageDisplayValue = currentPage && pages ? pages[currentPage - 1].toString() : "";
		});

		this.manualAnnotation$.pipe(
			takeUntil(this.destroy$)
		).subscribe((hasPdf) => this.showAnnotatedPdf = hasPdf);
	}

	public goToPage(pageNumber: number): void {
		this.store.dispatch(annotationEditorActions.goToPage({ pageNum: pageNumber }));
	}

	public requestImageGeneration() {
		this.store.dispatch(annotationEditorActions.createImage());
	}

	public updateEdits($event: RedactionAnnotationEdit[]) {
		this.store.dispatch(annotationEditorActions.updateAnnotationsForCurrentPage({ annotations: $event }));
	}

	public pageMenuOpened(): void {
		this.cdkViewPort.checkViewportSize();
		setTimeout(() => {
			this.cdkViewPort.scrollToIndex(this.currentPageNumber - 1);
		}, 10);
	}

}
